import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/homeData';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function handleScroll() {
      setIsScrolled(window.scrollY > 50);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 768) setMobileMenuOpen(false);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const textColor = isScrolled ? 'text-foreground-800' : 'text-white';
  const textHover = isScrolled ? 'hover:text-foreground-950' : 'hover:text-white/90';
  const logoColor = isScrolled ? 'text-foreground-950' : 'text-white';
  const logoSuffix = isScrolled ? 'text-foreground-600' : 'text-white/70';
  const iconColor = isScrolled ? 'text-secondary-500' : 'text-white';
  const mobileBtnBg = isScrolled ? 'hover:bg-background-100 text-foreground-800' : 'hover:bg-white/10 text-white';
  const dropdownArrow = isScrolled ? 'text-foreground-400' : 'text-white/70';

  return (
    <>
      <nav
        id="main-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70' : 'bg-transparent'}`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-18">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2 flex-shrink-0">
              <span className={`w-8 h-8 flex items-center justify-center text-xl ${iconColor}`}>
                <i className="ri-leaf-line"></i>
              </span>
              <span className={`text-lg font-heading tracking-tight ${logoColor}`}>
                itaobuy<span className={`font-light ${logoSuffix}`}>.homes</span>
              </span>
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-5">
              {/* Home */}
              <a
                href="/"
                className={`text-sm font-medium transition-colors whitespace-nowrap ${textColor} ${textHover}`}
              >
                Home
              </a>

              {/* Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textColor} ${textHover}`}
                >
                  Shop Categories
                  <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                    <i className={`ri-arrow-down-s-line text-xs ${dropdownArrow}`}></i>
                  </span>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 bg-background-50 rounded-xl p-5 w-[600px] animate-scale-in border border-background-200/70 shadow-lg">
                    <div className="grid grid-cols-2 gap-1.5">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors text-sm text-foreground-700 hover:text-foreground-950 whitespace-nowrap group"
                        >
                          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                            <img
                              src={cat.image}
                              alt={cat.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          <span className="font-medium">{cat.name}</span>
                          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Shopping Guide */}
              <button
                onClick={() => navigate('/article/itaobuyspreadsheet-complete-beginner-guide')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textColor} ${textHover}`}
              >
                Shopping Guide
              </button>

              {/* itaobuy sheet */}
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`text-sm font-medium transition-colors whitespace-nowrap ${textColor} ${textHover}`}
              >
                itaobuy sheet
              </a>

              {/* buy it now */}
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'bg-secondary-500 text-background-50 dark:text-foreground-950 hover:bg-secondary-600' : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'}`}
              >
                buy it now
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </span>
              </a>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${mobileBtnBg}`}
              aria-label="Toggle menu"
            >
              <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile side drawer overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
          ></div>

          {/* Drawer */}
          <div className="absolute right-0 top-0 bottom-0 w-[85vw] max-w-[380px] bg-background-50 animate-scale-in overflow-y-auto">
            <div className="p-5">
              {/* Close button */}
              <div className="flex items-center justify-between mb-6">
                <span className="text-base font-heading text-foreground-950">itaobuy.homes</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg text-foreground-600"></i>
                </button>
              </div>

              {/* Quick links */}
              <div className="flex flex-col gap-1 mb-6">
                <a
                  href="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors text-sm font-medium text-foreground-800"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-home-4-line text-foreground-500"></i>
                  </span>
                  Home
                </a>
                <button
                  onClick={() => { navigate('/article/itaobuyspreadsheet-complete-beginner-guide'); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors text-sm font-medium text-foreground-800 text-left cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-book-open-line text-foreground-500"></i>
                  </span>
                  Shopping Guide
                </button>
              </div>

              {/* Categories header */}
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-3 pt-3 pb-2 border-t border-background-200/70">
                Shop Categories
              </p>

              {/* Category list with images */}
              <div className="flex flex-col gap-1 mb-6">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors text-sm text-foreground-700 group"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <span>{cat.name}</span>
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                    </span>
                  </a>
                ))}
              </div>

              {/* CTA buttons */}
              <div className="border-t border-background-200/70 pt-4 flex flex-col gap-2.5">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-1.5 px-4 py-3 bg-foreground-950 text-background-50 rounded-full text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap"
                >
                  itaobuy sheet
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </span>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-1.5 px-4 py-3 bg-secondary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-secondary-600 transition-colors whitespace-nowrap"
                >
                  buy it now
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}